import { useSelector } from 'react-redux';

import { type UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import type { FuscaFleetAddress, FuscaFleetAddressType, FuscaFleetInfo, UUID, User } from '@cobliteam/common-types';

import { UserRequester } from '@cobliteam/api-lib';
import FuscaFleetRequester, { GraphQLFleetRequester } from '@cobliteam/api-lib/src/dashboard/fleet';

import { Environment } from '@cobliteam/react-environments';

import type { FleetUser } from '@cobliteam/api-lib/src/__generated__/graphql';
import type { AppState } from '@cobliteam/store';
import Alert from '@cobliteam/ui/components/Alert';

const env = Environment.getInstance().getEnvironment();
const userRequester = new UserRequester({ host: env.herbieApiHost });
const fuscaFleetRequester = new FuscaFleetRequester({ host: env.apiHost });
const graphqlFleetRequester = new GraphQLFleetRequester();

export const QUERY_KEY = ['FLEET'];
export const QUERY_KEY_FLEET_USERS = [...QUERY_KEY, 'USERS'];

const useFleet = () => {
  const session = useSelector((state: AppState) => state.login.session);
  const currentFleetId = session?.fleet.id;

  const getFleetId = (): UUID => {
    invariant(currentFleetId, 'Fleet id is not defined');
    return currentFleetId;
  };

  const getFleetName = (): string | undefined => {
    return session?.fleet.name;
  };

  // @deprecated use useGetFleetUser instead
  const getFleetUsers = (options?: Partial<UseQueryOptions<User[]>>) => {
    return useQuery<User[]>({
      queryKey: [...QUERY_KEY_FLEET_USERS, 'DEPRECATED'],
      queryFn: () => userRequester.getFleetUsers(getFleetId()),
      staleTime: Number.POSITIVE_INFINITY,
      ...options,
    });
  };

  const useGetFleetUsers = () => {
    return useQuery<FleetUser[]>({
      queryKey: [...QUERY_KEY_FLEET_USERS],
      queryFn: () => graphqlFleetRequester.getFleetUsers(getFleetId()),
    });
  };

  const useDeleteUser = () => {
    return useMutation<void, unknown, string>({
      mutationFn: async (token: string) => userRequester.deleteFleetUser(token),
      onSuccess: () => Alert.success('Usuário removido com sucesso!'),
      onError: () => Alert.error('Houve um erro, tente novamente mais tarde.'),
    });
  };

  return {
    getFleetId,
    getFleetName,
    getFleetUsers,
    useDeleteUser,
    useGetFleetUsers,
  };
};

const useGetFleetInfo = (fleetId: UUID, addressType?: FuscaFleetAddressType) => {
  return useQuery<FuscaFleetInfo>({
    queryKey: [QUERY_KEY, 'INFO', fleetId, addressType],
    queryFn: () => fuscaFleetRequester.getFleetInfo(fleetId, addressType),
  });
};

const useCreateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => fuscaFleetRequester.createFleetAddress(fleetId, info),
  });
};

const useUpdateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => fuscaFleetRequester.updateFleetAddress(fleetId, info),
  });
};

// FIXME: This is a temporary solution to get the user by id - api not implemented yet
const useGetUserById = ({ userId }: { userId: UUID }) => {
  const { useGetFleetUsers } = useFleet();

  const { data: fleetUsers, ...rest } = useGetFleetUsers();

  const user = fleetUsers?.find((user) => user.id === userId);

  return { data: user, ...rest };
};

export default useFleet;
export { useCreateFleetAddress, useGetFleetInfo, useGetUserById, useUpdateFleetAddress };
